import { computed, effect, Injectable, signal, untracked } from '@angular/core';
import { TemplateVariable } from '../interfaces/template-variable.interface';
import { templateEditorLogger } from '../logger.instance';

const logger = templateEditorLogger.extend('suggestion-service');

/**
 * Define o tipo de provedor de variáveis: pode ser um array estático de `TemplateVariable`
 * ou uma função de busca assíncrona que retorna uma `Promise` com as variáveis.
 */
export type VariableProvider =
  | TemplateVariable[]
  | ((searchTerm: string) => Promise<TemplateVariable[]>);

/**
 * Gerencia o estado e a lógica do dropdown de sugestões.
 * - Lida com a busca de variáveis (síncrona e assíncrona).
 * - Controla o estado de carregamento (`isLoading`).
 * - Gerencia a visibilidade do dropdown e o item ativo (`activeIndex`).
 * - Armazena o contexto que acionou o dropdown (`trigger`).
 */
@Injectable()
export class SuggestionService {
  /**
   * As sugestões de variáveis a serem exibidas no dropdown.
   */
  public suggestions = signal<TemplateVariable[]>([]);

  /**
   * Sinaliza se uma busca assíncrona por variáveis está em andamento.
   */
  public isLoading = signal(false);

  /**
   * Controla a visibilidade do dropdown de sugestões.
   */
  public isDropdownVisible = signal(false);

  /**
   * O índice do item atualmente ativo/selecionado na lista de sugestões.
   */
  public activeIndex = signal(0);

  /**
   * Informações sobre o que acionou o dropdown:
   * `position`: A posição do caractere no texto.
   * `char`: O caractere gatilho (ex: '{{').
   * `context`: O elemento DOM onde o gatilho ocorreu (pode ser um argumento de função).
   */
  public trigger = signal<{ char: string; context?: HTMLElement; position: number } | null>(null);

  /**
   * O termo de filtro atual digitado pelo usuário após o gatilho.
   */
  public filterTerm = signal('');

  /**
   * A posição (top, left) para renderizar o dropdown de sugestões.
   */
  public dropdownPosition = signal<{ left: string; top: string } | null>(null);

  private debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  private variableProvider!: VariableProvider;

  /**
   * Determina se o `variableProvider` é assíncrono (uma função) ou síncrono (um array).
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  public isAsync = computed(() => typeof this.variableProvider === 'function');

  public constructor() {
    // Efeito para resetar o índice ativo para 0 sempre que a lista de sugestões mudar.
    // `untracked` é usado para evitar um loop de dependência, pois a alteração de `activeIndex`
    // não deve re-acionar este efeito.
    effect(() => {
      this.suggestions(); // Depende da mudança em `suggestions`.
      untracked(() => this.activeIndex.set(0));
    });
  }

  /**
   * Inicializa o serviço com um provedor de variáveis (array ou função).
   *
   * @param provider O provedor de variáveis a ser usado.
   */
  public initialize(provider: VariableProvider): void {
    this.variableProvider = provider;
  }

  /**
   * Busca sugestões com base em um termo de pesquisa.
   * - Para provedores assíncronos, usa um `debounce` para evitar chamadas excessivas à API.
   * - Para provedores síncronos, filtra o array localmente.
   *
   * @param term O termo de pesquisa do usuário.
   * @param onResults Um callback executado com os resultados da busca, permitindo que
   *                  o `TemplateEditorService` atualize seu mapa de variáveis conhecidas.
   */
  public fetchSuggestions(term: string, onResults: (results: TemplateVariable[]) => void): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    this.filterTerm.set(term);
    const searchTerm = term.trim();

    if (this.isAsync()) {
      this.isLoading.set(true);
      const searchFn = this.variableProvider as (searchTerm: string) => Promise<TemplateVariable[]>;
      // Atraso de 300ms para esperar o usuário parar de digitar.
      this.debounceTimeout = setTimeout(async () => {
        try {
          const results = await searchFn(searchTerm);
          this.suggestions.set(results);
          onResults(results); // Informa o editor sobre as novas variáveis
        } catch (error) {
          logger.error('Falha ao buscar variáveis', error);
          this.suggestions.set([]);
        } finally {
          this.isLoading.set(false);
        }
      }, 300);

      return;
    }

    // Filtra o array estático.
    const allVars = this.variableProvider as TemplateVariable[];
    const lowerTerm = searchTerm.toLowerCase();
    const filtered = allVars.filter(
      (variable) =>
        variable.label.toLowerCase().includes(lowerTerm) ||
        variable.path.toLowerCase().includes(lowerTerm),
    );
    this.suggestions.set(filtered);
  }

  /**
   * Fecha o dropdown de sugestões e reseta todo o seu estado para o padrão.
   */
  public closeDropdown(): void {
    if (!this.isDropdownVisible()) {
      return;
    }

    this.isDropdownVisible.set(false);
    this.trigger.set(null);
    this.filterTerm.set('');
    this.activeIndex.set(0);
    this.suggestions.set([]);
    this.dropdownPosition.set(null);
  }
}
